<template>
  <div
    ref="wrapperRef"
    class="style-wrapper"
    :style="{ '--lang-spacing': getUCLang() === 'en-US' ? '0px' : '6px' }"
  >
    <!-- 入口按钮 -->
    <div
      v-if="!dialogOpen && !isLocalhost && isDelayOver"
      class="entrance-style"
      :class="{
        'entrance-type-rect': entranceProps.entranceType === EntranceType.RECT,
        'entrance-type-square': entranceProps.entranceType === EntranceType.SQUARE,
        'entrance-type-circle': entranceProps.entranceType === EntranceType.CIRCLE,
        'entrance-type-none': entranceProps.entranceType === EntranceType.INVISIBLE,
      }"
      :style="{
        '--width': `${entranceProps.width}px`,
        '--height': `${entranceProps.height}px`,
        '--bg-color': `${entranceProps.color}`,
        '--top': `${posTop}`,
        '--bottom': ``,
        '--left': `${posLeft}`,
        '--right': ``,
      }"
      @click="handleDialogOpen"
    >
      <img :src="getImgUrl('logo/white-logo', 'png')" :style="{ height: '20px', width: '20px' }" />
      <span
        v-if="!noText"
        class="entrance-label"
        :class="{
          'entrance-label-rect': entranceProps.entranceType === EntranceType.RECT,
          'entrance-label-square': entranceProps.entranceType === EntranceType.SQUARE,
          'entrance-label-circle': entranceProps.entranceType === EntranceType.CIRCLE,
        }"
        >{{ showLabel }}</span
      >
    </div>

    <!-- 聊天框 -->
    <ChatDialog
      v-if="(isLocalhost || dialogOpen) && isLoad"
      @close="handleDialogClose"
      :props="entranceProps"
      :dialog-style="dialogStyle"
    />
  </div>
</template>

<!-- eslint-disable @typescript-eslint/no-explicit-any -->
<script lang="ts">
/**
 * Purpose: Bootstrap the customer-facing Widget UI from channel settings, own
 * the entrance/open state, and normalize desktop dialog dimensions before render.
 * State: Owns entrance settings and the normalized dialog style; chat state remains in Pinia.
 * Props: config carries the channel identifier and optional untrusted backend style payload.
 */
import { computed, defineComponent, onMounted, onUnmounted, ref, watch } from 'vue'
import ChatDialog from './ChatDialog/ChatDialog.vue'
import { EntranceLocationType, EntranceType } from '../../enum/sessionEnum'
import { ChatStatus } from '../../enum/chatEnum'
import { useLang } from '../../hooks/useLang'
import { useWrapperSize } from '../../hooks/useWrapperHeight'
import { getImgUrl } from '../../utils/getImgUrl'
import { useChatStore } from '../../store/chat'
import { getLatestChat, getNavigation, getStyleSetting, getUserHistory } from '../../api/simple-api'
import { getNavigationFormat } from '../../utils/preprocessNavigation'
import {
  WIDGET_DIALOG_STYLE_CONSTRAINTS,
  normalizeWidgetDialogStyle,
} from '../../utils/widgetDialogStyle'
import { normalizeWidgetBrandStyle } from '../../utils/widgetBrandStyle'

interface Config {
  configId?: string
  styleSetting?: any | null
}

export default defineComponent({
  name: 'CustomerWindow',
  components: { ChatDialog },
  props: {
    config: {
      type: Object as () => Config,
      required: true,
    },
    containerId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const { getUCLang } = useLang() // zh-CN zh-TW en-US

    const wrapperRef = ref()

    const cStore = useChatStore()

    const isLoad = ref(false)

    const isDelayOver = ref(false)

    const { wrapperHeight, wrapperWidth } = useWrapperSize(wrapperRef)

    const IMG_URL = import.meta.env.VITE_IMG_BASE_URL

    const isLocalhost = computed(() => {
      return window.location.host === 'localhost:5100' || IMG_URL.includes(window.location.host)
    })

    const dialogOpen = ref<boolean>(false)

    const DEFAULT_ENTRANCE_PROPS = {
      id: '',
      channelId: '',
      channelName: '',
      remark: '',
      announcementFlag: false,
      announcement: {
        ['zh-CN']: '',
      },
      accessCode: '',
      linkCode: '',
      entranceType: 1,
      width: 44,
      height: 128,
      color: '#2e9b4a',
      location: 1,
      buttonText: {
        ['en-US']: 'Live Chat',
        ['zh-TW']: '在線客服',
        ['zh-CN']: '在线客服',
      },
      delay: '3000',
      boxColor: '#2E9B4A',
      logo: '',
      agentLogo: '',
      boxWidth: WIDGET_DIALOG_STYLE_CONSTRAINTS.width.default,
      boxHeight: WIDGET_DIALOG_STYLE_CONSTRAINTS.height.default,
      borderRadius: WIDGET_DIALOG_STYLE_CONSTRAINTS.borderRadius.default,
      robotId: '',
    }

    const entranceProps = ref(DEFAULT_ENTRANCE_PROPS)
    const dialogStyle = ref(normalizeWidgetDialogStyle(DEFAULT_ENTRANCE_PROPS))

    const mergeWithDefaults = (apiData: any) => {
      const result = { ...DEFAULT_ENTRANCE_PROPS } as any
      const source = apiData && typeof apiData === 'object' ? apiData : {}

      // 遍历所有键，只有不是null或undefined时才覆盖
      Object.keys(source).forEach((key) => {
        const value = source[key]
        if (value !== null && value !== undefined) {
          // 对于嵌套对象，需要特殊处理
          if (typeof value === 'object' && !Array.isArray(value)) {
            result[key] = { ...(result[key] || {}), ...value }
          } else {
            result[key] = value
          }
        }
      })

      return result
    }

    let wsConnected = false
    const ensureWsConnect = async () => {
      if (wsConnected) return
      wsConnected = true
      try {
        await cStore.wsConnect()
      } catch (e) {
        wsConnected = false
        throw e
      }
    }

    // 用户主动打开ChatDialog时建立连接（入口按钮 / window.MyAbot）
    watch(
      () => dialogOpen.value && isLoad.value,
      (shouldConnect) => {
        if (shouldConnect) ensureWsConnect()
      },
    )

    // 移动浏览器切后台后 JS 被冻结、超时后 WS 也会被 OS 杀掉；服务端在此期间的推送与
    // 状态变化在前端本地极难做对齐。简化方案：切回前台时若发现 WS 已断，直接触发一次
    // location.reload() 让服务端做唯一的真实来源，等价于"用户重新进了一次页面"。
    //
    // 仅在微信渠道（source=wechat）启用：这个救急方案本来就是为微信内置浏览器 H5 场景
    // 设计的。桌面场景下 wsIsConnected 门槛区分不了"OS 杀 WS"与"会话正常结束后 WS 被
    // 服务端关闭"两种情况——后者会误触发 reload，把已结束的会话反复重连。
    //
    // 800ms 延时是为了给 socket.io 检测连接状态的时间：JS 从冻结状态被唤醒后要几百毫秒
    // 才会 fire 'disconnect' 事件；直接同步读的话可能还是旧的 true。
    // 1.5s 的最小 hidden 时长阈值排除极短的窗口/tab 抖动，避免无谓地跑一次 setTimeout。
    const isWechatChannel = new URLSearchParams(window.location.search).get('source') === 'wechat'
    const HIDDEN_MIN_MS = 1500
    let hiddenAt = 0
    const onVisibilityChange = () => {
      if (!isWechatChannel) return
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now()
        return
      }
      if (document.visibilityState !== 'visible') return
      const hiddenDuration = hiddenAt ? Date.now() - hiddenAt : 0
      hiddenAt = 0
      if (hiddenDuration < HIDDEN_MIN_MS) return
      // 仅在真正建立过连接（dialog 已打开或 localhost 直连）的场景下才判定，避免未开启
      // widget 的宿主页面被误 reload。
      if (!wsConnected) return
      setTimeout(() => {
        if (document.visibilityState !== 'visible') return
        if (!cStore.wsIsConnected) {
          window.location.reload()
        }
      }, 800)
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    onUnmounted(() => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
    })

    onMounted(async () => {
      // 记录channelId configId就是channelId
      cStore.channelId = props.config.configId ?? '0'

      // 样式参数优先复用 index.js 拉取时已带过来的数据；缺失时回退调用接口
      let styleSetting = props.config.styleSetting
      if (!styleSetting) {
        const response = await getStyleSetting(props.config.configId)
        const data = await response.json()
        styleSetting = data.data
      }

      // 临时存取配置
      const mergedSettings = mergeWithDefaults(styleSetting)
      dialogStyle.value = normalizeWidgetDialogStyle(mergedSettings)
      const brandStyle = normalizeWidgetBrandStyle(mergedSettings.boxColor)
      entranceProps.value = {
        ...mergedSettings,
        boxColor: brandStyle.color,
        boxWidth: dialogStyle.value.width,
        boxHeight: dialogStyle.value.height,
        borderRadius: dialogStyle.value.borderRadius,
      }

      if (entranceProps.value.entranceType === EntranceType.INVISIBLE) {
        console.log('组件隐藏，设置全局MyAbot对象供用户操作')
        window.MyAbot = {
          open: () => {
            dialogOpen.value = true
          },
          close: () => {
            dialogOpen.value = false
          },
          toggle: () => {
            dialogOpen.value = !dialogOpen.value
          },
        }
      }

      cStore.agentAvatar = styleSetting?.agentLogo ?? ''
      console.log('配置获取成功:', entranceProps.value)

      if (entranceProps.value.robotId !== '') {
        console.log('机器人Id:' + entranceProps.value.robotId)
        const navData = await getNavigation(cStore.channelId)
        const navigationData = await navData.json()
        console.log('机器人导航栏获取成功:', navigationData.data)
        cStore.navigation = getNavigationFormat(navigationData.data, getUCLang())
      }

      // 最新会话是否已结束 → 刷新后应开启全新会话（旧会话归历史）
      let startFreshAfterTerminated = false
      // 确保获取到当前的chatId以及chat状态
      if (cStore.chatId === '0') {
        const latestChat = await getLatestChat(props.config.configId)
        const latestChatData = await latestChat.json()
        console.log('聊天信息获取成功:', latestChatData.data)
        if (latestChatData.data !== null) {
          if (latestChatData.data.status === ChatStatus.TERMINATION) {
            // 最新会话已结束：不接管为当前会话，开启全新空会话。
            // 仅保留 userId 以拉取历史；chatId 维持 '0'、chatInfo 维持空 → 干净的新会话，
            // 新会话的真实 chatId 由 WS chatInit 在打开聊天框后分配。
            startFreshAfterTerminated = true
            cStore.userId = latestChatData.data.userId
          } else {
            cStore.chatId = latestChatData.data.chatId
            cStore.chatStatus = latestChatData.data.status
            cStore.currentAgent = latestChatData.data.agentId
            // 0 排队中锁住输入框
            cStore.sendDisable = latestChatData.data.status === 0 ? true : false
            console.warn(`[latest]disable ${latestChatData.data.status === 0} text`)
            cStore.userId = latestChatData.data.userId
          }
        }
      }

      if (cStore.userId !== '0') {
        console.log('读到userId:', cStore.userId)
        const userHistory = await getUserHistory(props.config.configId)
        const userHistoryData = await userHistory.json()
        console.log('用户历史会话🆔')
        console.log(userHistoryData)
        // 归一化为 chatId 字符串列表：后端可能返回对象数组（含 chatId 等字段），
        // 而 findLastChatId 以字符串比较，故统一抽出 chatId，避免跨会话游标判断失效。
        cStore.chatHistory = (userHistoryData.data ?? []).map((item: any) =>
          typeof item === 'string' ? item : item.chatId,
        )
        // 全新会话(刚结束旧会话)时，历史游标从最近的历史会话(即刚结束的那个)开始，
        // 使其落到“查看历史消息”边界之后，而不是被当成当前会话直接展示。
        cStore.lastChatId = startFreshAfterTerminated
          ? (cStore.chatHistory[0] ?? '0')
          : cStore.chatId
        cStore.lastSequenceId = undefined
      }

      isLoad.value = true

      // isLocalhost模式下直接建立socket连接（不依赖dialogOpen）
      if (isLocalhost.value) {
        ensureWsConnect()
      }

      // 加延时
      console.log('开始计时')
      const delay = Number(entranceProps.value.delay)
      setTimeout(() => {
        console.log(delay + 'ms时间到')
        isDelayOver.value = true
      }, delay)
    })

    onUnmounted(() => {
      cStore.wsClose()
    })

    // 无文本样式判定
    const noText = computed(() => {
      return entranceProps.value.width === 44 && entranceProps.value.height === 44
    })

    // 顶部距离
    const posTop = computed(() => {
      const position = entranceProps.value.location
      if (position === EntranceLocationType.TOPLEFT || position === EntranceLocationType.TOPRIGHT) {
        return '0px'
      } else if (
        position === EntranceLocationType.MIDDLELEFT ||
        position === EntranceLocationType.MIDDLERIGHT
      ) {
        return `calc(${wrapperHeight.value / 2}px - var(--height) / 2 - 20px)`
      } else {
        return `calc(${wrapperHeight.value}px - var(--height) - 40px)`
      }
    })

    // 左距离
    const posLeft = computed(() => {
      const position = entranceProps.value.location
      if (
        position === EntranceLocationType.TOPRIGHT ||
        position === EntranceLocationType.MIDDLERIGHT ||
        position === EntranceLocationType.BOTTOMRIGHT
      ) {
        return `calc(${wrapperWidth.value}px - var(--width) - 40px)`
      } else {
        return '0px'
      }
    })

    // 访客入口的按钮文本
    const showLabel = computed(() => entranceProps.value.buttonText[getUCLang()])

    // 点击访客入口，打开客服聊天框
    const handleDialogOpen = () => {
      if (!isLocalhost.value) {
        dialogOpen.value = true
      }
    }

    const handleDialogClose = () => {
      if (!isLocalhost.value) {
        dialogOpen.value = false
      }
    }

    return {
      getImgUrl,
      getUCLang,
      showLabel,
      noText,
      entranceProps,
      dialogStyle,
      EntranceType,
      posTop,
      posLeft,
      wrapperRef,
      dialogOpen,
      handleDialogOpen,
      handleDialogClose,
      isLocalhost,
      isLoad,
      isDelayOver,
    }
  },
})
</script>

<style lang="less" scoped>
.style-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 999;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
}

.entrance-style {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  margin: 20px;
  gap: 8px;
  top: var(--top);
  left: var(--left);
  background-color: var(--bg-color);
  border-radius: 4px;
  transition: all 0.3s ease;
  width: var(--width);
  height: var(--height);
  cursor: pointer;

  &:hover {
    scale: 1.1;
  }

  &.entrance-type-circle {
    border-radius: 50%;
  }

  &.entrance-type-none {
    display: none;
  }
}

.entrance-label {
  color: white;
  user-select: none;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  &.entrance-label-rect {
    writing-mode: vertical-lr;
    letter-spacing: var(--lang-spacing);
    font-size: 14px;
    line-height: 20px;
    height: calc(var(--height) - 48px);
  }

  &.entrance-label-square {
    line-height: 14px;
    width: calc(var(--width) - 20px);
  }

  &.entrance-label-circle {
    line-height: 14px;
    width: calc(var(--width) - 20px);
  }
}
</style>
